/**
 * usePageBasket — the ordered page list for ONE case, restored and persisted.
 *
 * Built on useCaseEdits' discipline, deliberately, because the failure it
 * guards against is the same and cost five attempts to see there: the basket
 * and the case it belongs to are ONE piece of state. Held apart, retyping a
 * Case ID leaves a render where the previous case's list is in state while the
 * new id is current, and the save files one patient's pages under another's id.
 * A list whose `caseKey` is not the current case is, by construction, not this
 * case's list.
 *
 * With no stored list — a new case, or one stored for a different template —
 * the list starts EMPTY: the operator adds the pages they want (ADR 0015).
 * "Add every page of this template" fills it with the format's own pages, in the
 * template's order (ADR 0016), for when the whole report is wanted.
 */
import { useEffect, useRef, useState } from 'react'

import { canonicalFormatId, formatFor, buildable } from './formats.js'
import { newBasket, resetBasket, clearBasket, carrySeq } from './basket.js'
import { loadBasket, saveBasket } from './editStore.js'

export function usePageBasket(caseId, template, storage = undefined) {
  const caseKey = String(caseId ?? '').trim()
  const [state, setState] = useState({ caseKey: null, template: null, basket: null, dropped: 0 })
  // Which case the list on screen belongs to. A stored list for ANOTHER format
  // is reported, not replaced, when the case changes — the Builder switches the
  // picker to that format. Replacing only happens when the operator changes the
  // format themselves.
  const seenCase = useRef(null)
  const [storedFormat, setStoredFormat] = useState(null)

  const mine = state.caseKey === caseKey && state.template === template
  const basket = mine ? state.basket : null

  useEffect(() => {
    // A format with no page the Builder can build has no list to build.
    const format = formatFor(template)
    if (!format || !buildable(format)) {
      setState({ caseKey, template, basket: null, dropped: 0 })
      setStoredFormat(null)
      return
    }
    const got = loadBasket(caseKey, storage)
    const stored = got && canonicalFormatId(got.basket.template)
    const caseChanged = seenCase.current !== caseKey
    const previousCase = seenCase.current
    seenCase.current = caseKey
    if (got && stored === canonicalFormatId(template)) {
      setState({ caseKey, template, basket: got.basket, dropped: got.dropped })
      setStoredFormat(null)
      return
    }
    if (got && caseChanged) {
      // This case was built in another format. Keep its list and say so; the
      // Builder switches the picker rather than writing an empty list over it.
      // This is also what happens on the FIRST pick for a case whose list is in
      // another format: the operator has not been told the list exists, so it is
      // never emptied without them seeing whose it is. Picking again — now an
      // informed choice — replaces it.
      setState({ caseKey, template, basket: null, dropped: 0 })
      setStoredFormat(stored)
      return
    }
    // Pages built before the Case ID was typed belong to the case that is now
    // named. The operator can add pages first — the files come per page, so
    // nothing forces the Case ID to be first — and typing it must not throw that
    // work away. Only when the case holds no list of its own, so nothing is
    // overwritten.
    const adopting = !got && previousCase !== caseKey && caseKey
      && state.caseKey === '' && state.template === template && state.basket?.entries.length
    if (adopting) {
      setState({ caseKey, template, basket: state.basket, dropped: 0 })
      setStoredFormat(null)
      return
    }
    // The operator changed format for this case: a new list, keeping `seq`.
    // Captions are stored per case under the page's id, so a counter back at 0
    // would hand the old format's i1 captions to the new format's i1.
    setState({ caseKey, template, basket: carrySeq(newBasket(template), got?.basket), dropped: 0 })
    setStoredFormat(null)
  }, [caseKey, template, storage])

  // Persist — guarded on the list belonging to THIS case and template, so the
  // first render after a change cannot write the outgoing list under the
  // incoming id. See useCaseEdits for why the guard must not be removed.
  useEffect(() => {
    if (state.caseKey !== caseKey || state.template !== template || !state.basket) return
    saveBasket(caseKey, state.basket, storage)
  }, [state, caseKey, template, storage])

  /**
   * Apply a basket.js operation. Returns its `refused` sentence, or null.
   *
   * The refusal is read from a run against the list as rendered, because a
   * React state updater may run later than this function returns — a sentence
   * read from inside it would come back empty. The change itself is then
   * applied inside the updater, against the LATEST list, so two quick clicks
   * compose instead of the second overwriting the first. Operations are pure,
   * so running one twice is safe.
   */
  const apply = (op) => {
    if (!basket) return 'There is no page list for this case yet.'
    const preview = op(basket)
    if (preview.refused) return preview.refused
    setState((s) => {
      if (s.caseKey !== caseKey || s.template !== template || !s.basket) return s
      const r = op(s.basket)
      return r.refused ? s : { ...s, basket: r.basket }
    })
    return null
  }

  // Both keep `seq`, so no id is ever reissued (see basket.js).
  const own = (s) => (s.caseKey === caseKey && s.template === template ? s.basket : null)
  /** Every page of this template the Builder can build, in the template's order. */
  const startFromUsual = () => setState((s) => (
    formatFor(template) ? { ...s, caseKey, template, basket: resetBasket(own(s), template).basket } : s))
  /** An empty list. */
  const clear = () => setState((s) => (
    own(s) ? { ...s, basket: clearBasket(s.basket).basket } : s))

  return {
    basket,
    // The format a stored list for this case belongs to, when it is not the one
    // on screen. Null once the picker follows it.
    storedFormat,
    dropped: mine ? state.dropped : 0,
    apply,
    startFromUsual,
    clear,
  }
}
